import { QueryResult, ApolloError } from '@apollo/client';
import {
  Exact,
  MeProviderQuery,
  useUpdateProviderMutation,
} from '../../../../generated/graphql';
import { customToast } from '../../../components/ToastAlert/customToast';
import { applyColorToRoot } from '../../../lib/colors';
import { useBrandingImageUploadConstants } from '../../../hooks/useUIConstants';
import { LOGO_CONFIGS } from './constants';
import { LogoType } from './types';
import { useState, useCallback } from 'react';

export const useAppearanceHandlers = (
  setValue: any,
  refreshAuthedProviderUser: () => Promise<
    QueryResult<MeProviderQuery, Exact<{ [key: string]: never }>>
  >,
) => {
  const {
    maxLogoSize,
    maxWordmarkSize,
    maxBannerSize,
    acceptedBrandingImageTypes,
  } = useBrandingImageUploadConstants();
  const [updateProvider] = useUpdateProviderMutation();
  const [uploadingLogos, setUploadingLogos] = useState<
    Record<LogoType, boolean>
  >({
    squareLogoMediaUrl: false,
    wordmarkLogoMediaUrl: false,
    emailImageMediaUrl: false,
  });

  const isLogoUploading = useCallback(
    (logoType: LogoType): boolean => {
      return Boolean(uploadingLogos[logoType]);
    },
    [uploadingLogos],
  );

  const updateColor = async (
    color: string,
    field: 'primaryColor' | 'secondaryColor',
  ) => {
    try {
      await updateProvider({
        variables: { input: { [field]: color } },
      });

      applyColorToRoot(
        color,
        field === 'primaryColor' ? 'primary' : 'secondary',
      );

      customToast.success(
        `${field === 'primaryColor' ? 'Primary' : 'Secondary'} color updated!`,
      );
      await refreshAuthedProviderUser();
    } catch (err) {
      customToast.error('Failed to update color. Please try again later.');
    }
  };

  const validateLogoFile = (file: File, logoType: LogoType): boolean => {
    // Get the appropriate size limit based on logo type
    const maxSize =
      logoType === 'wordmarkLogoMediaUrl'
        ? maxWordmarkSize
        : logoType === 'emailImageMediaUrl'
        ? maxBannerSize
        : maxLogoSize;
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));

    if (file.size > maxSize) {
      customToast.error(
        `File is too big. Try one that's smaller than ${maxSizeMB} MB`,
      );
      return false;
    }
    if (!acceptedBrandingImageTypes.includes(file.type)) {
      customToast.error('Please upload a JPG or PNG file');
      return false;
    }
    return true;
  };

  const handleLogoUpload = async (
    files: FileList | null,
    logoType: LogoType,
  ) => {
    if (!files?.[0]) return;
    const file = files[0];

    if (!validateLogoFile(file, logoType)) return;

    setUploadingLogos((prev) => ({
      ...prev,
      [logoType]: true,
    }));

    const reader = new FileReader();
    reader.onload = async (event) => {
      const result = event?.target?.result;
      if (typeof result !== 'string') return;

      setValue(logoType, result);

      try {
        await updateProvider({
          variables: {
            input: { [LOGO_CONFIGS[logoType].graphqlField]: file },
          },
        });
        await refreshAuthedProviderUser();
        customToast.success(LOGO_CONFIGS[logoType].successMessage);
      } catch (err: unknown) {
        // Extract error message from GraphQL response
        const errorMessage =
          (err as ApolloError)?.graphQLErrors?.[0]?.message ||
          (err instanceof Error ? err.message : 'Failed to upload image');
        customToast.error(errorMessage);
      } finally {
        setUploadingLogos((prev) => ({
          ...prev,
          [logoType]: false,
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  const handleLogoDelete = async (logoType: LogoType) => {
    setValue(logoType, '');

    try {
      await updateProvider({
        variables: {
          input: {
            [LOGO_CONFIGS[logoType].deleteField]: true,
          },
        },
      });
      await refreshAuthedProviderUser();
      customToast.success(LOGO_CONFIGS[logoType].deleteMessage);
    } catch (err) {
      customToast.error('Failed to delete image');
    }
  };

  const updateSupportEmail = async (email: string) => {
    try {
      await updateProvider({
        variables: { input: { supportEmail: email } },
      });
      customToast.success('Support email updated!');
      await refreshAuthedProviderUser();
    } catch (err) {
      customToast.error(
        'Failed to update support email. Please try again later.',
      );
    }
  };

  return {
    updateColor,
    handleLogoUpload,
    handleLogoDelete,
    isLogoUploading,
    updateSupportEmail,
  };
};
